import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  type MRT_SortingState,
} from "material-react-table";
import { Button, Chip, MenuItem, Tooltip, Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import { GlobalModal } from "../../../components/GlobalModal";
import ManifestViewer from "../../../components/ManifestViewer";
import ReportPreviewModal from "../../cases/components/ReportPreviewModal";
import TableActionButton from "../../../components/table/TableActionButton";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useNavigate } from "react-router-dom";
import { fetchCasesBySuiteId, removeCase } from "../../../features/slices/Case";
import CaseAddForm from "./create_suite/CaseAddForm";
import { debounce } from "lodash";
import {
  fetchAllureSingleReport,
  fetchCaseReport,
} from "../../../features/slices/Report";
import { Case } from "../../../interfaces/Case";
import { useNotification } from "../../../context/NotificationContext";
import FormattedDateCell from "../../../components/FormattedDateCell";
const getStatusColor = (status: string) => {
  switch (status) {
    case "PASSED":
      return "#4CAF50";

    case "FAILED":
      return "#FF9800";

    case "FAILED_ENSURE":
      return "#FF9800";

    case "CMD_ERROR":
    case "INTERNAL_ERROR":
      return "#E53935";

    case "NO_TESTS":
      return "#9E9E9E";

    case "RESERVED":
      return "gray";

    case "INIT":
      return "#9E9E9E";

    default:
      return "#9E9E9E";
  }
};

const getResultColor = (result: string) => {
  switch (result) {
    case "SUCCESS":
      return "success";
    case "FAILURE":
    case "ERROR":
      return "error";
    default:
      return "default";
  }
};
const CasesTable = ({ suitId }: { suitId: number }) => {
  const [metaModal, setMetaModal] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [data, setData] = useState<Case[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [selectedMetadata, setSelectedMetadata] = useState<object>({});
  const [preview, setPreview] = useState<{
    open: boolean;
    caseId?: number;
    reportExists?: boolean;
  }>({ open: false });
  const [rowCount, setRowCount] = useState(0);
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const { user } = useSelector((state: RootState) => state.auth);
  const { notify } = useNotification();

  const handleMetaOpen = (metadata: object) => {
    setSelectedMetadata(metadata);
    setMetaModal(true);
  };

  const handleEdit = (id: number) => {
    navigate(`/cases/${id}/edit`);
  };

  const handleRemove = (id: number) => {
    dispatch(removeCase(id));
  };
  const handleDownload = async (caseId: number) => {
    try {
      const result = await dispatch(fetchCaseReport(Number(caseId))).unwrap();
      const url = window.URL.createObjectURL(
        new Blob([new Uint8Array(result.data)], {
          type: "application/zip",
        })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `case_${caseId}.zip`);
      document.body.appendChild(link);
      link.click();
      notify("Downloaded report.", "success");
    } catch (error) {
      console.error("Error downloading report:", error);
      notify("Failed to download report.", "error");
    }
  };
  const handleRefresh = () => {
    setCreateModal(false);
    fetchData();
  };
  const handleAllure = async (suiteId: number, caseId: number) => {
    if (!user) {
      notify("User is not authenticated", "error");
      return;
    }

    try {
      await dispatch(
        fetchAllureSingleReport({
          userId: user?.id || 0,
          suiteId: suiteId,
          caseId: caseId,
        })
      ).unwrap();
    } catch (error) {
      notify("Error fetching report", "error");
      console.error("Error:", error);
    }
  };
  const fetchData = useCallback(
    debounce(async () => {
      setIsRefetching(true);
      setIsLoading(true);
      try {
        const res = await dispatch(fetchCasesBySuiteId(suitId)).unwrap();
        setData(res.cases);
        setRowCount(res.total);
      } catch (error) {
        setIsError(true);
      } finally {
        setIsLoading(false);
        setIsRefetching(false);
      }
    }, 300),
    [dispatch, suitId]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData, columnFilters, globalFilter, sorting]);

  const columns = useMemo<MRT_ColumnDef<Case>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
      },
      {
        accessorKey: "allure",
        header: "Allure",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <TableActionButton
            label="Report"
            icon={<AssessmentOutlinedIcon />}
            onClick={() =>
              handleAllure(Number(row.original.suite_id), Number(row.id))
            }
            disabled={!row.original.report_exists}
            tooltip={
              row.original.report_exists
                ? "Open Allure report"
                : "No report yet — run the case first"
            }
          />
        ),
      },
      {
        accessorKey: "suite_id",
        header: "Suite ID",
      },
      {
        accessorKey: "title",
        header: "Title",
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue<string>()}>
            <span>{cell.getValue<string>()}</span>
          </Tooltip>
        ),
      },
      {
        accessorKey: "execute_at",
        header: "Execute At",
        Cell: ({ cell }) => <FormattedDateCell value={cell.getValue<Date>()} />,
      },
      {
        accessorKey: "tc_result",
        header: "TC Result",
        filterVariant: "select",
        Cell: ({ cell }) => (
          <Chip
            sx={{
              width: 150,
              backgroundColor: getStatusColor(cell.getValue<string>()),
              color: "#FFFFFF",
            }}
            label={cell.getValue<string>()}
          />
        ),
      },
      {
        accessorKey: "sys_result",
        header: "Sys Result",
        filterVariant: "select",
        Cell: ({ cell }) => (
          <Chip
            sx={{ width: 100 }}
            color={getResultColor(cell.getValue<string>())}
            label={cell.getValue<string>()}
          />
        ),
      },
      {
        accessorKey: "file_name",
        header: "File Name",
      },
      {
        accessorKey: "manifest",
        header: "Manifest",
        Cell: ({ cell }) => (
          <TableActionButton
            label="View"
            variant="text"
            icon={<VisibilityOutlinedIcon />}
            onClick={() => handleMetaOpen(cell.getValue<any>())}
          />
        ),
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data,
    mrtTheme: (theme) => ({
      baseBackgroundColor: theme.palette.background.default,
    }),
    muiTableContainerProps: {
      sx: { maxHeight: "calc(78vh - 112px)" },
    },
    muiPaginationProps: {
      color: "secondary",
      rowsPerPageOptions: [10, 25, 50, 100],
      shape: "rounded",
      variant: "outlined",
    },
    paginationDisplayMode: "pages",
    columnFilterDisplayMode: "popover",
    initialState: {
      pagination: { pageSize: 25, pageIndex: 0 },
      density: "compact",
      showGlobalFilter: true,
      columnPinning: {
        right: ["mrt-row-actions"],
      },
      columnVisibility: { sys_result: false },
    },
    enableColumnResizing: true,
    enableGrouping: false,
    enableRowSelection: false,
    enableRowActions: true,
    enableColumnOrdering: false,
    enableColumnPinning: true,
    enableStickyHeader: true,
    enableColumnFilterModes: true,
    positionActionsColumn: "last",
    enableFacetedValues: true,
    getRowId: (row) => String(row.id),
    layoutMode: "grid-no-grow",
    muiToolbarAlertBannerProps: isError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    rowCount,
    state: {
      columnFilters,
      globalFilter,
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      sorting,
    },
    renderRowActionMenuItems: ({ closeMenu, row }) => [
      <MenuItem
        key={0}
        onClick={() => {
          handleEdit(Number(row.id));
          closeMenu();
        }}
        sx={{ m: 0 }}
      >
        Edit
      </MenuItem>,
      <MenuItem
        key={1}
        onClick={() => {
          handleDownload(Number(row.id));
          closeMenu();
        }}
        sx={{ m: 0 }}
      >
        Download
      </MenuItem>,
      <MenuItem
        key={2}
        disabled={!row.original.report_exists}
        onClick={() => {
          setPreview({
            open: true,
            caseId: Number(row.id),
            reportExists: !!row.original.report_exists,
          });
          closeMenu();
        }}
        sx={{ m: 0 }}
      >
        Preview report
      </MenuItem>,
    ],
    renderTopToolbarCustomActions: ({ table }) => (
      <Box>
        <Button variant="contained" onClick={() => setCreateModal(true)}>
          Create New Case
        </Button>
      </Box>
    ),
  });

  return (
    <>
      <MaterialReactTable table={table} />
      <GlobalModal
        open={metaModal}
        onClose={() => setMetaModal(false)}
        title="Resource manifest"
        height="auto"
      >
        <ManifestViewer data={selectedMetadata} />
      </GlobalModal>
      <ReportPreviewModal
        open={preview.open}
        caseId={preview.caseId}
        reportExists={preview.reportExists}
        onClose={() => setPreview({ open: false })}
      />
      <GlobalModal
        open={createModal}
        onClose={() => setCreateModal(false)}
        title="Create New Case"
      >
        <CaseAddForm suiteId={suitId} onClose={handleRefresh} />
      </GlobalModal>
    </>
  );
};

export default CasesTable;
